import { useEffect, useRef } from 'react';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useWalletDisconnect from 'src/hooks/wallet-management/useWalletDisconnect';
import { useDisconnectWallets } from 'src/hooks/wallets/useDisconnectWallets';
import useModal from 'src/components/Modals/hooks/useModal';
import { notify } from 'src/utils/notifications';
import { markPrivateBetaWalletRejected } from 'src/hooks/privateBetaWalletWhitelist/privateBetaWalletWhitelistState';
import { usePrivateBetaWalletWhitelistCheck } from 'src/hooks/privateBetaWalletWhitelist/usePrivateBetaWalletWhitelistCheck';
import { ONBOARDING_CONNECTION_STEP_LOCK_KEY } from 'src/utils/WalletConnectionState';

/**
 * Runs once app-wide. After wallet connect, checks the private beta whitelist
 * and disconnects wallets that are definitively not whitelisted. A wallet is
 * only disconnected on an explicit negative verdict — a transient API/network
 * failure never force-disconnects a (possibly whitelisted) wallet.
 */
export function usePrivateBetaWalletWhitelistGateOnConnect() {
	const {
		shouldCheck,
		authority,
		connected,
		isWhitelisted,
		isLoading,
		isFetched,
		isError,
	} = usePrivateBetaWalletWhitelistCheck();

	const disconnectWallets = useDisconnectWallets();
	const { handleWalletDisconnect } = useWalletDisconnect();
	const { remove } = useModal();
	const walletConnectionState = useCurrentWalletConnectionState();

	// Tracked separately so an error followed by a definitive rejection still
	// disconnects, while neither the error toast nor the disconnect repeats.
	const erroredAuthorityRef = useRef<string | null>(null);
	const rejectedAuthorityRef = useRef<string | null>(null);

	useEffect(() => {
		if (!connected) {
			erroredAuthorityRef.current = null;
			rejectedAuthorityRef.current = null;
		}
	}, [connected]);

	useEffect(() => {
		if (!shouldCheck || isLoading || !isFetched || !authority) {
			return;
		}

		// Could not verify: surface a distinct message and leave the wallet
		// connected — do not mark it rejected. Onboarding stays blocked by
		// `usePrivateBetaWalletWhitelistStatus` until the check succeeds.
		if (isError) {
			if (erroredAuthorityRef.current === authority) {
				return;
			}
			erroredAuthorityRef.current = authority;
			notify({
				type: 'error',
				message: 'Could not verify private beta access',
				description:
					'We could not verify whether this wallet has private beta access. Please try again shortly.',
			});
			return;
		}

		// Only a definitive `false` results in a disconnect.
		if (isWhitelisted !== false) {
			return;
		}

		if (rejectedAuthorityRef.current === authority) {
			return;
		}

		rejectedAuthorityRef.current = authority;
		markPrivateBetaWalletRejected(authority);

		void (async () => {
			await disconnectWallets();
			handleWalletDisconnect();
			walletConnectionState.instance.update('NotConnected', true);
			walletConnectionState.instance.update(
				'Connecting',
				false,
				ONBOARDING_CONNECTION_STEP_LOCK_KEY
			);
			remove('connect_wallet');

			notify({
				type: 'error',
				message: 'Private beta access required',
				description:
					'This wallet is not on the private beta access list. Connect with an approved wallet to continue.',
			});
		})();
	}, [
		authority,
		disconnectWallets,
		handleWalletDisconnect,
		isError,
		isFetched,
		isLoading,
		isWhitelisted,
		shouldCheck,
		remove,
		walletConnectionState.instance,
	]);
}
